import React, { createContext, useContext, useEffect, useCallback, useState, useMemo } from 'react';
import { safeFetch } from '../../lib/api';
import { auth } from '../../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getActiveWorkspaceId, setActiveWorkspaceId,
  type Workspace, type WorkspacesMe,
} from '../../lib/workspaces';

export interface AccessDeniedInfo {
  requestedSlug: string | null;
  requestedWorkspaceId: string | null;
}

interface WorkspaceContextValue {
  memberships: Workspace[];
  activeWorkspace: Workspace | null;
  activeWorkspaceId: string;
  loading: boolean;
  platformAdmin: boolean;
  /** Set when the user is authenticated but NOT a member of the requested workspace. */
  accessDenied: AccessDeniedInfo | null;
  switchWorkspace: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue>({
  memberships: [],
  activeWorkspace: null,
  activeWorkspaceId: '',
  loading: true,
  platformAdmin: false,
  accessDenied: null,
  switchWorkspace: async () => {},
  refresh: async () => {},
});

export function useWorkspaces(): WorkspaceContextValue {
  return useContext(WorkspaceContext);
}

/** Apply a workspace's branding to the CSS custom properties (overrides env brand).
 *  Mirrors applyBrandToDOM so ALL brand colors (--brand-* AND the Tailwind
 *  blue scale the UI uses) follow the workspace's brand config. */
function applyWorkspaceBranding(branding: Workspace['branding'] | undefined) {
  const root = document.documentElement;
  if (!branding) return;
  const vars: Record<string, string | undefined> = {
    '--brand-primary': branding.primary,
    '--brand-primary-light': branding.primaryLight,
    '--brand-primary-dark': branding.primaryDark,
    '--brand-accent': branding.accent,
  };
  for (const [name, value] of Object.entries(vars)) {
    if (value) root.style.setProperty(name, value);
  }
  // Rebuild the blue scale so bg-blue-*/text-blue-* utilities follow the brand.
  const primary = branding.primary || '#2563eb';
  const light = branding.primaryLight || '#dbeafe';
  const dark = branding.primaryDark || '#1d4ed8';
  const mix = (a: string, w: number, b: string) => `color-mix(in srgb, ${a} ${Math.round(w * 100)}%, ${b})`;
  const blueScale: Record<string, string> = {
    '--color-blue-50': mix(light, 0.6, '#ffffff'),
    '--color-blue-100': light,
    '--color-blue-200': mix(primary, 0.18, '#ffffff'),
    '--color-blue-300': mix(primary, 0.34, '#ffffff'),
    '--color-blue-400': mix(primary, 0.58, '#ffffff'),
    '--color-blue-500': mix(primary, 0.82, '#ffffff'),
    '--color-blue-600': primary,
    '--color-blue-700': dark,
    '--color-blue-800': mix(dark, 0.84, '#000000'),
    '--color-blue-900': mix(dark, 0.68, '#000000'),
    '--color-blue-950': mix(dark, 0.52, '#000000'),
  };
  for (const [name, value] of Object.entries(blueScale)) {
    root.style.setProperty(name, value);
  }
  // Favicon (logo by default, favicon if provided) + title.
  const favUrl = branding.faviconUrl || branding.logoUrl;
  if (favUrl) {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = favUrl;
  }
  if (branding.businessName) {
    document.title = branding.businessName + ' Portal';
  }
}

/**
 * Global workspace provider. Loads the user's workspace memberships once they
 * are signed in, exposes the active workspace + a switcher, and applies the
 * active workspace's branding. Mounted once in App.tsx around the routed app.
 */
export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [memberships, setMemberships] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setWorkspaceId] = useState<string>(() => getActiveWorkspaceId());
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState<AccessDeniedInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUid, setAuthUid] = useState<string | null>(() => auth.currentUser?.uid || null);

  // Keep the workspace context in sync with sign-in state (login/logout).
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const nextUid = u?.uid || null;
      setAuthUid(prev => (prev === nextUid ? prev : nextUid));
    });
    return unsub;
  }, []);

  const refresh = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setMemberships([]);
      setActiveWorkspace(null);
      setPlatformAdmin(false);
      setAccessDenied(null);
      setLoading(false);
      return;
    }
    try {
      const data: WorkspacesMe = await safeFetch('/api/workspaces/me');
      setMemberships(data.memberships || []);
      setPlatformAdmin(!!data.platformAdmin);
      // Access Denied: the user explicitly requested a workspace (via the URL
      // subdomain / header) that they are NOT a member of. Show the Access
      // Denied experience — NEVER silently switch them into another workspace.
      if (data.requestedWorkspaceId && data.isRequestedMember === false) {
        setAccessDenied({ requestedSlug: data.requestedSlug, requestedWorkspaceId: data.requestedWorkspaceId });
        setLoading(false);
        return;
      }
      setAccessDenied(null);
      const stored = getActiveWorkspaceId();
      const valid = stored && data.memberships?.some(m => m.workspaceId === stored)
        ? stored
        : data.activeWorkspaceId || data.memberships?.[0]?.workspaceId || '';
      if (valid) {
        setWorkspaceId(valid);
        setActiveWorkspaceId(valid);
        const match = data.memberships?.find(m => m.workspaceId === valid) || data.workspace;
        setActiveWorkspace(match || null);
        applyWorkspaceBranding(match?.branding || data.workspace?.branding);
      } else {
        // No valid membership anywhere — clear any stale stored workspace id so
        // subsequent requests stop sending an X-Workspace-Id the user can no
        // longer access (e.g. they were removed from it while signed in).
        setWorkspaceId('');
        setActiveWorkspace(null);
        setActiveWorkspaceId('');
      }
    } catch (e) {
      console.warn('[workspaces] failed to load memberships:', e);
    } finally {
      setLoading(false);
    }
  }, [authUid]);

  useEffect(() => {
    refresh();
  }, [refresh, authUid]);

  const switchWorkspace = useCallback(async (id: string) => {
    const next = memberships.find(m => m.workspaceId === id);
    if (!next) return; // membership required — never switch into an unauthorized workspace
    setWorkspaceId(id);
    setActiveWorkspaceId(id);
    setActiveWorkspace(next);
    setAccessDenied(null);
    applyWorkspaceBranding(next.branding);
    // Navigate to the workspace's own custom domain if configured, or subdomain,
    // so the URL becomes the active workspace context.
    let targetUrl = '';
    if (next.customDomains && next.customDomains.length > 0) {
      targetUrl = `https://${next.customDomains[0]}`;
    } else {
      const { workspaceSubdomainUrl } = await import('../../lib/platformDomain');
      targetUrl = workspaceSubdomainUrl(next.slug);
    }
    if (typeof window !== 'undefined' && targetUrl && new URL(targetUrl).hostname !== window.location.hostname) {
      window.location.assign(targetUrl);
    } else {
      window.location.reload();
    }
  }, [memberships]);

  const value = useMemo(() => ({
    memberships,
    activeWorkspace,
    activeWorkspaceId,
    loading,
    platformAdmin,
    accessDenied,
    switchWorkspace,
    refresh,
  }), [memberships, activeWorkspace, activeWorkspaceId, loading, platformAdmin, accessDenied, switchWorkspace, refresh]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
